// 赠品促销功能
class GiftPromotion {
  constructor(config) {
    this.config = config;
    this.debug = config.debug || false; // 可选的调试模式
    this.init();
  }
  
  // 调试日志方法
  log(...args) {
    if (this.debug) {
      console.log('[GiftPromotion]', ...args);
    }
  }
  
  warn(...args) {
    if (this.debug) {
      console.warn('[GiftPromotion]', ...args);
    }
  }
  
  error(...args) {
    if (this.debug) {
      console.error('[GiftPromotion]', ...args);
    }
  }

  init() {
    if (!this.config.enabled || !this.config.giftProduct) {
      return;
    }
    
    this.isAddingGift = false; // 防止重复添加的标志
    this.checkAvailability();
    this.setupEventListeners();
    this.checkInitialCartState();
  }

  // 检查初始购物车状态
  checkInitialCartState() {
    const giftVariantId = this.getGiftVariantId();
    
    if (!giftVariantId) {
      return;
    }
    
    // 只检查赠品是否已经在购物车中，不自动添加
    // 自动添加只通过 ajaxProduct:added 事件触发
    this.checkGiftInCart(giftVariantId).then(giftInCart => {
      if (giftInCart) {
        // 如果赠品已经在购物车中，标记为已添加
        this.markGiftAsAdded();
      }
    }).catch(() => {});
  }

  // 标记赠品已添加
  markGiftAsAdded() {
    const giftModule = document.querySelector('.gift-promotion-module');
    if (giftModule) {
      giftModule.classList.add('gift-added');
    }
  }

  // 检查赠品促销可用性
  checkAvailability() {
    const discountCode = this.config.discountCode;
    
    // 如果没有折扣码，说明是自动折扣模式，直接显示模块
    if (!discountCode || discountCode.trim() === '') {
      this.showModule(this.config.maxQuantity || 999);
      return;
    }
    
    // 有折扣码，说明是手动折扣模式，需要检查折扣码可用性
    fetch(`/discount/${discountCode}`)
      .then(response => response.json())
      .then(data => {
        const usedCount = data.usage_count || 0;
        const remaining = this.config.maxQuantity - usedCount;
        
        if (remaining > 0) {
          this.showModule(remaining);
        } else {
          this.hideModule();
        }
      })
      .catch(error => {
        // 容错处理：默认显示
        this.showModule(this.config.maxQuantity || 999);
      });
  }

  // 显示赠品促销模块
  showModule(remaining) {
    const giftModule = document.querySelector('.gift-promotion-module');
    if (giftModule) {
      giftModule.style.display = 'block';
      
      // 更新剩余数量
      const remainingElement = document.querySelector('.gift-remaining-count');
      if (remainingElement) {
        remainingElement.textContent = remaining;
      }
      
      // 检查赠品是否已经在购物车中
      const giftVariantId = this.getGiftVariantId();
      if (giftVariantId) {
        this.checkGiftInCart(giftVariantId).then(giftInCart => {
          if (giftInCart) {
            this.markGiftAsAdded();
          }
        });
      }
    }
  }

  // 检查购物车中是否有主产品（用于自动折扣模式）
  checkCartForMainProduct() {
    return fetch(theme.routes.cart_url + '.js')
      .then(response => response.json())
      .then(cart => {
        // 检查购物车中是否有任何产品（除了赠品本身）
        if (!cart.items || cart.items.length === 0) {
          return false;
        }
        
        const giftVariantId = this.getGiftVariantId();
        // 如果购物车中有产品，且不是只有赠品，说明有主产品
        const hasNonGiftItems = cart.items.some(item => {
          return giftVariantId ? item.variant_id !== giftVariantId : true;
        });
        
        return hasNonGiftItems;
      })
      .catch(error => {
        return false;
      });
  }

  // 隐藏赠品促销模块
  hideModule() {
    const giftModule = document.querySelector('.gift-promotion-module');
    if (giftModule) {
      giftModule.style.display = 'none';
    }
  }

  // 获取赠品变体 ID
  getGiftVariantId() {
    if (!this.config.giftProduct) {
      return null;
    }

    // 如果 giftProduct 是字符串（产品 handle），需要通过 API 获取产品信息
    if (typeof this.config.giftProduct === 'string') {
      return null;
    }

    // 首先尝试使用 selected_or_first_available_variant
    if (this.config.giftProduct.selected_or_first_available_variant) {
      return this.config.giftProduct.selected_or_first_available_variant.id;
    }

    // 如果没有，尝试从 variants 数组中获取第一个可用变体
    if (this.config.giftProduct.variants && Array.isArray(this.config.giftProduct.variants)) {
      const firstAvailableVariant = this.config.giftProduct.variants.find(v => v.available !== false) || this.config.giftProduct.variants[0];
      if (firstAvailableVariant && firstAvailableVariant.id) {
        return firstAvailableVariant.id;
      }
    }

    // 如果都没有，尝试直接使用 giftProduct.id（如果它是变体 ID）
    if (this.config.giftProduct.id) {
      return this.config.giftProduct.id;
    }

    return null;
  }

  // 通过产品 handle 获取产品信息
  async fetchProductByHandle(handle) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const product = await response.json();
      
      // 更新配置中的 giftProduct
      this.config.giftProduct = {
        id: product.id,
        title: product.title,
        handle: product.handle,
        featured_image: product.featured_image,
        selected_or_first_available_variant: product.selected_or_first_available_variant,
        variants: product.variants
      };
      
      return product.selected_or_first_available_variant?.id || product.variants?.[0]?.id || null;
    } catch (error) {
      return null;
    }
  }

  // 计算购物车中主产品的总数量
  async getMainProductQuantity() {
    try {
      const cart = await (await fetch(theme.routes.cart_url + '.js')).json();
      const giftVariantId = this.getGiftVariantId();
      
      // 计算所有非赠品产品的数量总和
      let totalQuantity = 0;
      if (cart.items) {
        cart.items.forEach(item => {
          // 跳过赠品本身
          if (item.variant_id !== giftVariantId) {
            totalQuantity += item.quantity;
          }
        });
      }
      
      this.log('购物车中主产品总数量:', totalQuantity);
      return totalQuantity;
    } catch (error) {
      this.error('获取主产品数量失败:', error);
      return 0;
    }
  }

  // 获取购物车中赠品的当前数量
  async getGiftQuantityInCart(giftVariantId) {
    try {
      const cart = await (await fetch(theme.routes.cart_url + '.js')).json();
      const giftItem = cart.items.find(item => item.variant_id === giftVariantId);
      return giftItem ? giftItem.quantity : 0;
    } catch (error) {
      this.error('获取赠品数量失败:', error);
      return 0;
    }
  }

  // 自动添加或更新赠品到购物车
  async addGiftToCart() {
    let giftVariantId = this.getGiftVariantId();
    
    // 如果 giftProduct 是字符串（handle），需要通过 API 获取
    if (!giftVariantId && typeof this.config.giftProduct === 'string') {
      this.log('giftProduct 是 handle，通过 API 获取产品信息');
      giftVariantId = await this.fetchProductByHandle(this.config.giftProduct);
    }
    
    if (!giftVariantId) {
      this.warn('无法获取赠品变体 ID，无法添加赠品');
      return;
    }

    // 如果正在添加中，避免重复添加
    if (this.isAddingGift) {
      this.log('正在添加赠品中，跳过重复请求');
      return;
    }
    
    this.log('开始检查并添加赠品，variantId:', giftVariantId);
    this.isAddingGift = true;
    
    try {
      // 获取主产品数量和赠品当前数量
      const mainProductQty = await this.getMainProductQuantity();
      const currentGiftQty = await this.getGiftQuantityInCart(giftVariantId);
      
      this.log('主产品数量:', mainProductQty, '赠品当前数量:', currentGiftQty);
      
      if (mainProductQty === 0) {
        this.log('购物车中没有主产品，跳过添加赠品');
        this.isAddingGift = false;
        return;
      }
      
      // 计算需要添加或更新的赠品数量
      const neededGiftQty = mainProductQty;
      
      if (currentGiftQty === neededGiftQty) {
        this.log('赠品数量已正确，无需更新');
        this.markGiftAsAdded();
        this.isAddingGift = false;
        return;
      }
      
      if (currentGiftQty === 0) {
        // 赠品不在购物车中，添加对应数量的赠品
        this.log('赠品不在购物车中，添加', neededGiftQty, '个赠品');
        await this.addGiftToCartAPI(giftVariantId, neededGiftQty);
      } else {
        // 赠品已在购物车中，更新数量
        this.log('更新赠品数量从', currentGiftQty, '到', neededGiftQty);
        await this.updateGiftQuantity(giftVariantId, neededGiftQty);
      }
      
      this.markGiftAsAdded();
    } catch (error) {
      this.error('添加或更新赠品失败:', error);
    } finally {
      this.isAddingGift = false;
    }
  }

  // 检查赠品是否已在购物车中
  checkGiftInCart(giftVariantId) {
    return fetch(theme.routes.cart_url + '.js')
      .then(response => response.json())
      .then(cart => {
        return cart.items.some(item => item.variant_id === giftVariantId);
      })
      .catch(error => {
        return false;
      });
  }

  // 通过 API 添加赠品到购物车（完全按照主题 theme.js 的方式）
  addGiftToCartAPI(giftVariantId, quantity = 1) {
    this.log('调用 API 添加赠品，variantId:', giftVariantId, 'quantity:', quantity);
    
    // 收集需要更新的 sections（与主题完全一致）
    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { 
      bubbles: true, 
      detail: { sections: sectionsToBundle } 
    }));
    
    this.log('收集到的 sections:', sectionsToBundle);
    
    // 使用主题的 fetchConfig（与 theme.js line 5284 完全一致）
    const config = theme.utils.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    
    // 创建 FormData（与 theme.js line 5288-5290 完全一致）
    const formData = new FormData();
    formData.append('id', giftVariantId);
    formData.append('quantity', quantity.toString());
    formData.append('properties[_gift_promotion]', 'true');
    if (this.config.discountCode) {
      formData.append('properties[_gift_discount_code]', this.config.discountCode);
    }
    formData.append('sections', sectionsToBundle);
    formData.append('sections_url', window.location.pathname);
    
    config.body = formData;

    // 发送请求（与 theme.js line 5303-5345 逻辑一致）
    return fetch(theme.routes.cart_add_url, config)
      .then((response) => response.json())
      .then(async (parsedState) => {
        this.log('API 响应数据:', parsedState);
        
        // 检查是否有错误
        if (parsedState.status) {
          this.error('添加赠品失败:', parsedState.description || parsedState.message);
          throw new Error(parsedState.description || parsedState.message);
        }

        // 获取完整的购物车数据（与主题完全一致）
        const cartJson = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET')})).json();
        cartJson['sections'] = parsedState['sections'];

        this.log('合并 sections 数据，准备发布 cartUpdate 事件');

        // 发布购物车更新事件（与主题完全一致）
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { 
          source: 'gift-promotion', 
          productVariantId: giftVariantId,
          cart: cartJson 
        });

        // 触发自定义事件
        document.dispatchEvent(new CustomEvent('giftPromotionAdded', {
          detail: {
            giftProduct: this.config.giftProduct,
            variantId: giftVariantId,
            quantity: quantity
          }
        }));

        this.log('赠品添加成功，数量:', quantity);
        return cartJson;
      });
  }

  // 更新赠品数量（使用 cart/change.js API）
  async updateGiftQuantity(giftVariantId, newQuantity) {
    this.log('更新赠品数量，variantId:', giftVariantId, 'newQuantity:', newQuantity);
    
    try {
      // 获取购物车中赠品的 line item key
      const cart = await (await fetch(theme.routes.cart_url + '.js')).json();
      const giftItem = cart.items.find(item => item.variant_id === giftVariantId);
      
      if (!giftItem) {
        this.warn('购物车中未找到赠品，无法更新数量');
        return;
      }

      // 收集需要更新的 sections
      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { 
        bubbles: true, 
        detail: { sections: sectionsToBundle } 
      }));

      // 使用 cart/change.js API 更新数量
      const body = JSON.stringify({
        id: giftItem.key,
        quantity: newQuantity,
        sections: sectionsToBundle
      });

      const response = await fetch(theme.routes.cart_change_url, { 
        ...theme.utils.fetchConfig(), 
        body 
      });
      
      const parsedState = await response.json();
      
      this.log('更新赠品数量成功');

      // 发布购物车更新事件
      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { 
        source: 'gift-promotion', 
        cart: parsedState 
      });

      return parsedState;
    } catch (error) {
      this.error('更新赠品数量失败:', error);
      throw error;
    }
  }

  // 获取赠品促销表单数据（现在赠品是自动添加的，这个方法保留用于兼容性）
  getFormData() {
    const formData = {
      items: []
    };
    
    // 赠品现在通过 addGiftToCart 方法自动添加
    // 这个方法保留用于向后兼容
    return formData;
  }

  // 显示成功弹窗
  showModal() {
    if (!this.config.enabled || !this.config.giftProduct) {
      return;
    }

    const modal = document.getElementById('gift-promotion-modal');
    const giftProduct = this.config.giftProduct;
    
    // 填充弹窗内容
    if (document.getElementById('gift-promotion-modal-img')) {
      document.getElementById('gift-promotion-modal-img').src = giftProduct.featured_image || '';
      document.getElementById('gift-promotion-modal-img').alt = giftProduct.title || '';
    }
    if (document.getElementById('gift-promotion-modal-name')) {
      document.getElementById('gift-promotion-modal-name').textContent = giftProduct.title || '';
    }
    
    // 获取价格
    let price = 0;
    if (giftProduct.selected_or_first_available_variant && giftProduct.selected_or_first_available_variant.price) {
      price = giftProduct.selected_or_first_available_variant.price;
    } else if (giftProduct.variants && giftProduct.variants.length > 0) {
      const firstVariant = giftProduct.variants.find(v => v.available !== false) || giftProduct.variants[0];
      price = firstVariant.price || 0;
    }
    
    if (document.getElementById('gift-promotion-modal-original-price')) {
      document.getElementById('gift-promotion-modal-original-price').textContent = (price / 100).toFixed(2);
    }
    
    // 更新剩余数量
    const remainingElement = document.querySelector('.gift-remaining-count');
    if (remainingElement) {
      document.getElementById('gift-promotion-modal-remaining').textContent = remainingElement.textContent;
    }
    
    // 显示弹窗
    modal.style.display = 'flex';
    
    // 添加事件监听器
    this.setupModalEvents();
  }

  // 设置弹窗事件监听器
  setupModalEvents() {
    const modal = document.getElementById('gift-promotion-modal');
    const closeBtn = document.querySelector('.gift-promotion-modal-close');
    const continueBtn = document.querySelector('.gift-promotion-modal-btn.continue-shopping');
    const checkoutBtn = document.querySelector('.gift-promotion-modal-btn.checkout');
    
    // 关闭弹窗
    const closeModal = () => {
      modal.style.display = 'none';
    };
    
    // 点击关闭按钮
    if (closeBtn) {
      closeBtn.onclick = closeModal;
    }
    
    // 点击背景关闭
    modal.onclick = function(event) {
      if (event.target === modal) {
        closeModal();
      }
    };
    
    // 继续购物
    if (continueBtn) {
      continueBtn.onclick = function() {
        closeModal();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      };
    }
    
    // 立即结算
    if (checkoutBtn) {
      checkoutBtn.onclick = function() {
        this.applyDiscount();
        window.location.href = '/checkout';
      }.bind(this);
    }
  }

  // 应用折扣码（仅手动折扣模式使用）
  applyDiscount() {
    const discountCode = this.config.discountCode;
    
    if (!discountCode || discountCode.trim() === '') {
      return;
    }
    
    fetch(theme.routes.cart_update_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        attributes: {
          'discount_code': discountCode
        }
      })
    })
    .then(response => response.json())
    .then(data => {
      // 静默处理
    })
    .catch(error => {
      // 静默处理
    });
  }

  // 设置事件监听器
  setupEventListeners() {
    // 监听赠品添加成功事件
    document.addEventListener('giftPromotionAdded', (event) => {
      this.showModal();
    });

    // 只在产品添加到购物车时触发（参考 theme.js 的实现）
    // 这是最可靠的方式，避免在删除产品时误触发
    const handleProductAdded = (event) => {
      this.log('监听到 ajaxProduct:added 事件');
      
      // 如果是自动折扣模式（没有折扣码），自动添加赠品
      if (!this.config.discountCode || this.config.discountCode.trim() === '') {
        this.log('自动折扣模式，准备添加赠品');
        
        // 检查添加的产品是否是赠品本身，如果是则跳过
        const addedProductVariantId = event.detail?.product?.id || event.detail?.product?.variant_id;
        const giftVariantId = this.getGiftVariantId();
        
        if (addedProductVariantId && giftVariantId && addedProductVariantId.toString() === giftVariantId.toString()) {
          this.log('添加的是赠品本身，跳过');
          return;
        }
        
        // 延迟一下，确保主产品已经添加到购物车，并且购物车已经更新
        setTimeout(() => {
          this.addGiftToCart().catch(error => {
            this.error('添加赠品失败:', error);
          });
        }, 1000);
      }
    };
    document.addEventListener('ajaxProduct:added', handleProductAdded);

    // 监听购物车更新事件，检查是否需要同步赠品数量
    if (window.theme && window.theme.pubsub && window.theme.pubsub.PUB_SUB_EVENTS) {
      const handleCartUpdate = (event) => {
        // 如果是自动折扣模式（没有折扣码），检查赠品数量
        if (!this.config.discountCode || this.config.discountCode.trim() === '') {
          // 忽略来自 gift-promotion 的更新事件，避免循环
          if (event.source === 'gift-promotion') {
            return;
          }
          
          // 当购物车数量变化时（来自 cart-items），同步赠品数量
          if (event.source === 'cart-items' && event.cart) {
            this.log('监听到购物车数量变化，检查赠品数量');
            setTimeout(() => {
              this.addGiftToCart().catch(error => {
                this.error('同步赠品数量失败:', error);
              });
            }, 500);
          }
        }
      };
      this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, handleCartUpdate);
    }
  }
}

// 初始化赠品促销功能
document.addEventListener('DOMContentLoaded', function() {
  // 从全局变量获取配置
  if (window.giftPromotionConfig) {
    window.giftPromotion = new GiftPromotion(window.giftPromotionConfig);
  }
});
